import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AccountType, ReEvent, Id, RolesForEvent, Topic, UserDetails } from "../../interfaces/types";
import uuidv4 from "uuid-random";
import { useCustomThemeContext } from "../../providers/customThemeProvider";
import { useEventContext } from "../../providers/EventProvider";
import { uploadImage } from "../../services/firebaseService";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, ExpandMore } from "@mui/icons-material";
import DividerHorizontal from "../dividers/DividerHorizontal";
import EventTextInputSection from "../inputs/EventTextInputSection";
import EventMultipleDateTimePicker from "../inputs/EventMultipleDateTimePicker";
import ImagePicker from "../inputs/ImagePicker";
import EventDateTimePicker from "../inputs/EventDateTimePicker";
import ColorPicker from "../inputs/ColorPicker";
import EventSubmitButton from "../buttons/EventSubmitButton";
import { MuiColorInput } from "mui-color-input";
import { useSessionContext } from "../../providers/SessionProvider";
import { usePermissionContext } from "../../providers/PermissionProvider";
import { useUserContext } from "../../providers/userProvider";

// Define the props for the EventForm component
interface EventFormProps {
  editForm?: boolean;
  event?: ReEvent;
  submitButtonText: string;
}

// Default values for a new event
const defaultEvent: ReEvent = {
  id: uuidv4(),
  name: "",
  description: "",
  startDate: null,
  endDate: null,
  sessions: [],
  eventConfiguration: {
    dashboardTitle: "",
    dashboardWelcomeText: "",
    dashboardPictureUrl: "",
    logoUrl: "",
    faviconUrl: "",
    websiteUrl: "",
    targetAudience: "",
    submissionStartDate: null,
    submissionEndDate: null,
    votingStartDate: null,
    votingEndDate: null,
    programDate: null,
    hasCustomStyling: false,
    primaryColor: "#000",
    secondaryColor: "#000",
    tertiaryColor: "#000",
    warningColor: "#000",
    infoColor: "#000",
    errorColor: "#000",
    linkColor: "#000",
    clickedLinkColor: "#000",
    topics: [{
      id: uuidv4(),
      name: "Topic",
      color: "#000"
    }],
    roomSetups: [{
      id: uuidv4(),
      name: "roomSetup",
    }],
    possibleDurations: [{
      id: uuidv4(),
      duration: 30,
    }],
    useXpFactor: false,
    areReviewsAndCommentsVisible: false,
    isReviewCreationActive: false,
    isCommentCreationActive: false,

  },
};

// Define the EventForm functional component
export const EventForm: FC<EventFormProps> = ({
  editForm = false,
  event: eventToUpdate = defaultEvent,
  submitButtonText,
}) => {
  const navigate = useNavigate();
  const { theme, updateTheme } = useCustomThemeContext();
  const { addEvent, updateEvent } = useEventContext();
  const [event, setEvent] = useState<ReEvent>(eventToUpdate);
  const [dashboardPictureUpload, setDashboardPictureUpload] =
    useState<File | string>(eventToUpdate.eventConfiguration.dashboardPictureUrl || "");
  const [logoUpload, setLogoUpload] =
    useState<File | string>(eventToUpdate.eventConfiguration.logoUrl || "");
  const [faviconUpload, setFaviconUpload] =
    useState<File | string>(eventToUpdate.eventConfiguration.faviconUrl || "");
  const [isBeingSubmitted, setIsBeingSubmitted] = useState(false);
  const [dashboardPictureUploadUrl, setDashboardPictureUploadUrl] =
    useState<string>(eventToUpdate.eventConfiguration.dashboardPictureUrl || "");
  const [logoUploadUrl, setLogoUploadUrl] = useState<string>(
    eventToUpdate.eventConfiguration.logoUrl || ""
  );
  const [faviconUploadUrl, setFaviconUploadUrl] = useState<string>(
    eventToUpdate.eventConfiguration.faviconUrl || ""
  );
  const { isTopicUsedBySession, isRoomSetupUsedBySession } = useSessionContext();
  const { hasEventManagerSelectionPermission } = usePermissionContext();
  const [selectedManagers, setSelectedManagers] = useState<UserDetails[]>([]);
  const [prevManagers, setPrevManagers] = useState<UserDetails[]>([]);
  const { getEventManagers, getPossibleEventManagers, userDetails, updateEventRoleOnUsers } = useUserContext();

  useEffect(() => {
    if (editForm) {
      const prevManagers = getEventManagers(event.id);
      setPrevManagers(prevManagers);
      setSelectedManagers(prevManagers);
    }
  }, [event.id]);

  // Upload images when they are selected
  useEffect(() => {
    const uploadImages = async () => {
      if (dashboardPictureUpload) {
        if (dashboardPictureUpload instanceof File) {
          try {
            setDashboardPictureUploadUrl(
              await uploadImage(dashboardPictureUpload, "dashboardPictures")
            );
          } catch (error) {
            console.log(error);
          }
        } else {
          setDashboardPictureUploadUrl(dashboardPictureUpload as string); // if string, it is already an url
        }
      }
      if (logoUpload) {
        if (logoUpload instanceof File) {
          try {
            setLogoUploadUrl(await uploadImage(logoUpload, "logos"));
          } catch (error) {
            console.log(error);
          }
        } else {
          setLogoUploadUrl(logoUpload as string); // if string, it is already an url
        }
      }
      if (faviconUpload) {
        if (faviconUpload instanceof File) {
          try {
            setFaviconUploadUrl(await uploadImage(faviconUpload, "favicons"));
          } catch (error) {
            console.log(error);
          }
        } else {
          setFaviconUploadUrl(faviconUpload as string); // if string, it is already an url
        }
      }
    };

    uploadImages();
  }, [logoUpload, dashboardPictureUpload, faviconUpload]);

  // Update the event state when the image URLs change
  useEffect(() => {
    const updateEventState = () => {
      setEvent({
        ...event,
        eventConfiguration: {
          ...event.eventConfiguration,
          dashboardPictureUrl: dashboardPictureUploadUrl,
          logoUrl: logoUploadUrl,
          faviconUrl: faviconUploadUrl,
        },
      });
    };
    updateEventState();
  }, [dashboardPictureUploadUrl, logoUploadUrl, faviconUploadUrl]);

  // Function to change the color in the event configuration
  function changeColor(color: string, newValue: string) {
    if (event && event.eventConfiguration) {
      switch (color) {
        case "primary":
          event.eventConfiguration.primaryColor = newValue;
          break;
        case "secondary":
          event.eventConfiguration.secondaryColor = newValue;
          break;
        case "tertiary":
          event.eventConfiguration.tertiaryColor = newValue;
          break;
        case "warning":
          event.eventConfiguration.warningColor = newValue;
          break;
        case "info":
          event.eventConfiguration.infoColor = newValue;
          break;
        case "error":
          event.eventConfiguration.errorColor = newValue;
          break;
        case "link":
          event.eventConfiguration.linkColor = newValue;
          break;
        case "clickedLink":
          event.eventConfiguration.clickedLinkColor = newValue;
          break;
      }
      setEvent({ ...event });
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    setIsBeingSubmitted(true);

    //prevent empty event submission
    if (!event.name
      || !event.description
      || !event.eventConfiguration.targetAudience
      || !event.startDate
      || !event.endDate
      || !event.eventConfiguration.dashboardTitle
      || !event.eventConfiguration.dashboardWelcomeText
      || (event.eventConfiguration.topics && event.eventConfiguration.topics.some(topic => !topic.name))) {
      setIsBeingSubmitted(false);
      return;
    }

    try {
      if (editForm) {
        updateEvent(event);
      } else {
        addEvent(event);
      }
      updateEventRoleOnUsers(event.id, RolesForEvent.MANAGER, prevManagers, selectedManagers);
      navigate("/event/" + event.id);

    } catch (error) {
      console.error("Error creating event: ", error);
    }
    finally { setIsBeingSubmitted(false); }
  };



  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container disableGutters>

        <Box sx={{ mt: 3, width: "70%", justifySelf: "center", textAlign: "center" }}>
          <Typography variant="h5" mt={3}>Event Details</Typography>
          {/*settings*/}
          <Container disableGutters sx={{ mt: 3 }}>
            {/*general settings*/}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="general-panel-content"
                id="general-panel-header"
              >
                <Typography variant="h6">General settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                {/* name of event */}
                <EventTextInputSection
                  event={event}
                  setEvent={setEvent}
                  label="Name of event"
                  fieldName="name"
                  required
                />
                {/* date of event */}
                <EventMultipleDateTimePicker
                  event={event}
                  setEvent={setEvent}
                  label="Date of event"
                  fieldNameStart={"startDate"}
                  fieldNameEnd={"endDate"}
                  required
                />
                {/* description of event */}
                <EventTextInputSection
                  event={event}
                  setEvent={setEvent}
                  label="Description"
                  fieldName="description"
                  required
                />
                {/* target audience of event */}
                <EventTextInputSection
                  event={event}
                  setEvent={setEvent}
                  label="Target audience"
                  fieldName="targetAudience"
                  subfield
                  required
                />
                {/* website of event */}
                <EventTextInputSection
                  event={event}
                  setEvent={setEvent}
                  label="Website URL"
                  fieldName="websiteUrl"
                  subfield
                />
              </AccordionDetails>
            </Accordion>
            {/* dashboard settings */}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="dashboard-panel-content"
                id="dashboard-panel-header"
              >
                <Typography variant="h6">Dashboard settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                <EventTextInputSection
                  event={event}
                  setEvent={setEvent}
                  label="Dashboard title"
                  fieldName="dashboardTitle"
                  subfield
                  required
                />
                <EventTextInputSection
                  event={event}
                  setEvent={setEvent}
                  label="Dashboard welcome text"
                  fieldName="dashboardWelcomeText"
                  subfield
                  required
                />
              </AccordionDetails>
            </Accordion>
            {/* image settings */}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="images-panel-content"
                id="images-panel-header"
              >
                <Typography variant="h6">Image settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                <ImagePicker
                  label="Dashboard picture"
                  defaultValue={event?.eventConfiguration?.dashboardPictureUrl}
                  onImageSelect={(image) => setDashboardPictureUpload(image)}
                  folder="dashboardPictures"
                />
                <ImagePicker
                  label="Logo"
                  defaultValue={event?.eventConfiguration?.logoUrl}
                  onImageSelect={(image) => setLogoUpload(image)}
                  folder="logos"
                />
                <ImagePicker
                  label="Favicon"
                  defaultValue={event?.eventConfiguration?.faviconUrl}
                  onImageSelect={(image) => setFaviconUpload(image)}
                  folder="favicons"
                  iconsOnly
                />
              </AccordionDetails>
            </Accordion>
            {/* Deadline settings */}
            {/*<Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="deadline-panel-content"
                id="deadline-panel-header"
              >
                <Typography variant="h6">Deadline settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                {/* submission dates */}
            {/*<EventMultipleDateTimePicker
                  event={event}
                  setEvent={setEvent}
                  label="Submission"
                  fieldNameStart={"submissionStartDate"}
                  fieldNameEnd={"submissionEndDate"}
                  subfield
                />
                {/* voting dates */}
            {/*<EventMultipleDateTimePicker
                  event={event}
                  setEvent={setEvent}
                  label="Voting"
                  fieldNameStart={"votingStartDate"}
                  fieldNameEnd={"votingEndDate"}
                  subfield
                />
                {/* program date */}
            {/*<EventDateTimePicker
                  event={event}
                  setEvent={setEvent}
                  label="Program"
                  fieldName={"programDate"}
                  subfield
                />
              </AccordionDetails>
            </Accordion>*/}
            {/* Topic settings */}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="topic-panel-content"
                id="topic-panel-header"
              >
                <Typography variant="h6">Topics options</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack direction={"column"} spacing={2} mt={2}>
                    {event.eventConfiguration.topics.length > 0 && event.eventConfiguration.topics.map((topic, index) => (
                      <Stack direction={"row"} spacing={2} key={index}>
                        <TextField
                          required
                          id={`outlined-topic-${index}`}
                          label={`Topic ${index + 1}`}
                          variant="outlined"
                          placeholder="name of the topic"
                          value={topic.name}
                          onChange={(e) => {
                            setEvent({
                              ...event,
                              eventConfiguration: {
                                ...event.eventConfiguration,
                                topics: event.eventConfiguration.topics.map((t, i) => {
                                  if (i === index) {
                                    return { ...t, name: e.target.value };
                                  }
                                  return t;
                                }),
                              }
                            });
                          }}
                        />
                        <MuiColorInput
                          format="hex"
                          value={topic.color}
                          onChange={(value) => {
                            setEvent({
                              ...event,
                              eventConfiguration: {
                                ...event.eventConfiguration,
                                topics: event.eventConfiguration.topics.map((t, i) => {
                                  if (i === index) {
                                    return { ...t, color: value };
                                  }
                                  return t;
                                }),
                              }
                            });
                          }}
                        />
                        {event.eventConfiguration.topics.length > 1 && !isTopicUsedBySession(topic.id) &&
                          <IconButton size="small"
                            onClick={() => {
                              const newEvent = {
                                ...event,
                                eventConfiguration: {
                                  ...event.eventConfiguration,
                                  topics: event.eventConfiguration.topics.filter((_, i) => i !== index)
                                }
                              };
                              setEvent(newEvent);
                            }}
                            sx={{
                              backgroundColor: theme.palette.error.main,
                              alignSelf: "center"
                            }}>
                            <Delete sx={{ color: "white" }} />
                          </IconButton>
                        }
                      </Stack>
                    ))}
                  </Stack>
                  <Stack direction={"column"} spacing={2} mt={2}>
                    <IconButton
                      onClick={() => {
                        const newEvent = {
                          ...event,
                          eventConfiguration: {
                            ...event.eventConfiguration,
                            topics: [...event.eventConfiguration.topics, {
                              id: uuidv4(),
                              name: defaultEvent.eventConfiguration.topics[0].name,
                              color: defaultEvent.eventConfiguration.topics[0].color
                            }]
                          }
                        };
                        setEvent(newEvent);
                      }}
                      sx={{
                        backgroundColor: theme.palette.primary.main
                      }}
                    >
                      <Add sx={{ color: "white" }} />
                    </IconButton>
                  </Stack>
                </Stack>

              </AccordionDetails>
            </Accordion>
            {/* Room settings */}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="room-panel-content"
                id="room-panel-header"
              >
                <Typography variant="h6">Room options</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack direction={"column"} spacing={2} mt={2}>
                    {event.eventConfiguration.roomSetups && event.eventConfiguration.roomSetups.length > 0 && event.eventConfiguration.roomSetups.map((room, index) => (
                      <Stack direction={"row"} spacing={2} key={index}>
                        <TextField
                          required
                          id={`outlined-roomsetup-${index}`}
                          label={`Room ${index + 1}`}
                          variant="outlined"
                          placeholder="name of the room"
                          value={room.name}
                          onChange={(e) => {
                            setEvent({
                              ...event,
                              eventConfiguration: {
                                ...event.eventConfiguration,
                                roomSetups: event.eventConfiguration.roomSetups.map((r, i) => {
                                  if (i === index) {
                                    return { ...r, name: e.target.value };
                                  }
                                  return r;
                                }),
                              }
                            });
                          }}
                        />
                        {event.eventConfiguration.roomSetups.length > 1 && !isRoomSetupUsedBySession(room.id) &&
                          <IconButton size="small"
                            onClick={() => {
                              const newEvent = {
                                ...event,
                                eventConfiguration: {
                                  ...event.eventConfiguration,
                                  roomSetups: event.eventConfiguration.roomSetups.filter((_, i) => i !== index)
                                }
                              };
                              setEvent(newEvent);
                            }}
                            sx={{
                              backgroundColor: theme.palette.error.main,
                              alignSelf: "center"
                            }}>
                            <Delete sx={{ color: "white" }} />
                          </IconButton>
                        }
                      </Stack>
                    ))}
                  </Stack>
                  <Stack direction={"column"} spacing={2} mt={2}>
                    <IconButton
                      onClick={() => {
                        const newEvent = {
                          ...event,
                          eventConfiguration: {
                            ...event.eventConfiguration,
                            roomSetups: [...event.eventConfiguration.roomSetups, { id: uuidv4(), name: defaultEvent.eventConfiguration.roomSetups[0].name }]
                          }
                        };
                        setEvent(newEvent);
                      }}
                      sx={{
                        backgroundColor: theme.palette.primary.main
                      }}
                    >
                      <Add sx={{ color: "white" }} />
                    </IconButton>
                  </Stack>
                </Stack>

              </AccordionDetails>
            </Accordion>
            {/* Duration settings */}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="duration-panel-content"
                id="duration-panel-header"
              >
                <Typography variant="h6">Duration options</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack direction={"column"} spacing={2} mt={2}>
                    {event.eventConfiguration.possibleDurations && event.eventConfiguration.possibleDurations.length > 0 && event.eventConfiguration.possibleDurations.map((possibleDuration, index) => (
                      <Stack direction={"row"} spacing={2} key={index}>
                        <TextField
                          required
                          id={`outlined-duration-${index}`}
                          label={`Duration ${index + 1}`}
                          variant="outlined"
                          placeholder="duration in minutes"
                          type="number"
                          slotProps={{
                            htmlInput: {
                              min: 5,
                              max: 480,
                              step: 5
                            }
                          }}
                          value={possibleDuration.duration}
                          onChange={(e) => {
                            setEvent({
                              ...event,
                              eventConfiguration: {
                                ...event.eventConfiguration,
                                possibleDurations: event.eventConfiguration.possibleDurations.map((d, i) => {
                                  if (i === index) {
                                    return { ...d, duration: parseInt(e.target.value) };
                                  }
                                  return d;
                                }),
                              }
                            });
                          }}
                        />
                        {event.eventConfiguration.possibleDurations.length > 1 &&
                          <IconButton size="small"
                            onClick={() => {
                              const newEvent = {
                                ...event,
                                eventConfiguration: {
                                  ...event.eventConfiguration,
                                  possibleDurations: event.eventConfiguration.possibleDurations.filter((_, i) => i !== index)
                                }
                              };
                              setEvent(newEvent);
                            }}
                            sx={{
                              backgroundColor: theme.palette.error.main,
                              alignSelf: "center"
                            }}>
                            <Delete sx={{ color: "white" }} />
                          </IconButton>
                        }
                      </Stack>
                    ))}
                  </Stack>
                  <Stack direction={"column"} spacing={2} mt={2}>
                    <IconButton
                      onClick={() => {
                        const newEvent = {
                          ...event,
                          eventConfiguration: {
                            ...event.eventConfiguration,
                            possibleDurations: [...event.eventConfiguration.possibleDurations, { id: uuidv4(), duration: defaultEvent.eventConfiguration.possibleDurations[0].duration }]
                          }
                        };
                        setEvent(newEvent);
                      }}
                      sx={{
                        backgroundColor: theme.palette.primary.main
                      }}
                    >
                      <Add sx={{ color: "white" }} />
                    </IconButton>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
            {/* Color settings */}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="color-panel-content"
                id="color-panel-header"
              >
                <Typography variant="h6">Color settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                <FormControlLabel
                  sx={{ width: "100%", mt: 2 }}
                  value="use custom styling"
                  control={
                    <Switch
                      checked={event?.eventConfiguration?.hasCustomStyling}
                      onChange={() =>
                        setEvent({
                          ...event,
                          eventConfiguration: {
                            ...event.eventConfiguration,
                            hasCustomStyling:
                              !event.eventConfiguration.hasCustomStyling,
                          },
                        })
                      }
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label="use custom styling"
                  labelPlacement="end"
                />
                {event?.eventConfiguration?.hasCustomStyling && (
                  <Box
                    sx={{
                      justifyContent: "space-between",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box flex={"50%"}>
                      <ColorPicker
                        name="Primary"
                        outline="left"
                        value={event?.eventConfiguration?.primaryColor}
                        changeColor={(value) => changeColor("primary", value)}
                      />
                      <ColorPicker
                        name="Secondary"
                        outline="left"
                        value={event?.eventConfiguration?.secondaryColor}
                        changeColor={(value) => changeColor("secondary", value)}
                      />
                      <ColorPicker
                        name="Tertiary"
                        outline="left"
                        value={event?.eventConfiguration?.tertiaryColor}
                        changeColor={(value) => changeColor("tertiary", value)}
                      />
                      <ColorPicker
                        name="Link"
                        outline="left"
                        value={event?.eventConfiguration?.linkColor}
                        changeColor={(value) => changeColor("link", value)}
                      />
                    </Box>
                    <Divider
                      orientation="vertical"
                      variant="middle"
                      flexItem
                      sx={{
                        borderRight: 1,
                        borderColor: theme.palette.primary.main,
                      }}
                    />
                    <Box flex={"50%"}>
                      <ColorPicker
                        name="Warning"
                        outline="right"
                        value={event?.eventConfiguration?.warningColor}
                        changeColor={(value) => changeColor("warning", value)}
                      />
                      <ColorPicker
                        name="Info"
                        outline="right"
                        value={event?.eventConfiguration?.infoColor}
                        changeColor={(value) => changeColor("info", value)}
                      />
                      <ColorPicker
                        name="Error"
                        outline="right"
                        value={event?.eventConfiguration?.errorColor}
                        changeColor={(value) => changeColor("error", value)}
                      />
                      <ColorPicker
                        name="Clicked Link"
                        outline="right"
                        value={event?.eventConfiguration?.clickedLinkColor}
                        changeColor={(value) =>
                          changeColor("clickedLink", value)
                        }
                      />
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
            {/*review and comment settings*/}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="general-panel-content"
                id="general-panel-header"
              >
                <Typography variant="h6">Review / comment settings</Typography>
              </AccordionSummary>
                <AccordionDetails>
                <DividerHorizontal />
                <FormControlLabel
                  sx={{ width: "100%", mt: 2 }}
                  control={
                  <Switch
                    checked={event?.eventConfiguration?.areReviewsAndCommentsVisible}
                    onChange={() =>
                    setEvent({
                      ...event,
                      eventConfiguration: {
                      ...event.eventConfiguration,
                      areReviewsAndCommentsVisible:
                        !event.eventConfiguration.areReviewsAndCommentsVisible,
                      isReviewCreationActive: event.eventConfiguration.areReviewsAndCommentsVisible ? event.eventConfiguration.isReviewCreationActive : false,
                      isCommentCreationActive: event.eventConfiguration.areReviewsAndCommentsVisible ? event.eventConfiguration.isCommentCreationActive : false,
                      },
                    })
                    }
                    inputProps={{ "aria-label": "controlled" }}
                  />
                  }
                  label="Reviews and comments visible"
                  labelPlacement="end"
                />
                <FormControlLabel
                  sx={{ width: "100%", mt: 2 }}
                  control={
                  <Switch
                    checked={event?.eventConfiguration?.isReviewCreationActive && event?.eventConfiguration?.areReviewsAndCommentsVisible}
                    onChange={() => {
                    if (!event?.eventConfiguration?.areReviewsAndCommentsVisible) {
                      alert("Reviews and comments must be visible first");
                      return;
                    }
                    setEvent({
                      ...event,
                      eventConfiguration: {
                      ...event.eventConfiguration,
                      isReviewCreationActive:
                        !event.eventConfiguration.isReviewCreationActive,
                      },
                    });
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                  }
                  label="Creation of reviews active"
                  labelPlacement="end"
                />
                <FormControlLabel
                  sx={{ width: "100%", mt: 2 }}
                  control={
                  <Switch
                    checked={event?.eventConfiguration?.isCommentCreationActive && event?.eventConfiguration?.areReviewsAndCommentsVisible}
                    onChange={() => {
                    if (!event?.eventConfiguration?.areReviewsAndCommentsVisible) {
                      alert("Reviews and comments must be visible first");
                      return;
                    }
                    setEvent({
                      ...event,
                      eventConfiguration: {
                      ...event.eventConfiguration,
                      isCommentCreationActive:
                        !event.eventConfiguration.isCommentCreationActive,
                      },
                    });
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                  }
                  label="Creation of comments active"
                  labelPlacement="end"
                />
                </AccordionDetails>
            </Accordion>
            {/* Manager settings */}
            {hasEventManagerSelectionPermission(event.id) &&
              <Accordion
                sx={{ border: 1, borderColor: theme.palette.primary.main }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="color-panel-content"
                  id="color-panel-header"
                >
                  <Typography variant="h6">Manager settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DividerHorizontal />
                  <Box
                    sx={{
                      justifyContent: "space-between",
                      display: "flex",
                      alignItems: "center",
                      mt: 2
                    }}
                  >
                    {/*event-managers*/}
                    {<FormControl fullWidth>
                      <Autocomplete
                        multiple
                        spellCheck
                        id="managerSelection"
                        value={selectedManagers}
                        onChange={(event, newValue) => {
                          setSelectedManagers([...newValue]);
                        }}
                        options={getPossibleEventManagers()}
                        getOptionLabel={(option) => `${option?.firstName ?? ""} ${option?.lastName ?? ""}`}
                        renderTags={(tagValue, getTagProps) =>
                          tagValue.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                avatar={<Avatar alt={"avatar van " + option?.firstName + " " + option?.lastName} src={option.profileImageUrl} />}
                                key={uuidv4()}
                                label={option?.firstName + " " + option?.lastName}
                                {...tagProps}
                                variant="outlined"
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Eventmanager(s)" placeholder="Add Eventmanager" />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={uuidv4()}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                <Avatar alt={"avatar van " + option?.firstName + "" + option?.lastName} src={option.profileImageUrl} sx={{ mr: 2 }} />
                                {option?.firstName} {option?.lastName}
                              </Box>
                              {userDetails?.accountType === AccountType.ADMIN &&
                                option.eventRoles.some(eventrole => eventrole.eventId === event.id && eventrole.roles.includes(RolesForEvent.MANAGER)) &&
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexGrow: 0 }}>
                                  <Typography>M</Typography>
                                </Box>
                              }
                            </Box>
                          </li>
                        )}
                        noOptionsText={"No possible eventmanagers found"}
                      /></FormControl>}
                  </Box>
                </AccordionDetails>
              </Accordion>}
            {/*other settings*/}
            <Accordion
              sx={{ border: 1, borderColor: theme.palette.primary.main }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="general-panel-content"
                id="general-panel-header"
              >
                <Typography variant="h6">Other settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DividerHorizontal />
                <FormControlLabel
                  sx={{ width: "100%", mt: 2 }}
                  control={
                    <Switch
                      checked={event?.eventConfiguration?.useXpFactor}
                      onChange={() =>
                        setEvent({
                          ...event,
                          eventConfiguration: {
                            ...event.eventConfiguration,
                            useXpFactor:
                              !event.eventConfiguration.useXpFactor,
                          },
                        })
                      }
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label="use XP-factor"
                  labelPlacement="end"
                />
              </AccordionDetails>
            </Accordion>

          </Container>

          <EventSubmitButton
            isBeingSubmitted={isBeingSubmitted}
            handleSubmit={handleSubmit}
            text={submitButtonText}
          />
        </Box>


      </Container>
    </LocalizationProvider>
  );
};

export default EventForm;
function getEventManagers(id: Id): import("react").SetStateAction<UserDetails[]> {
  throw new Error("Function not implemented.");
}

